import { reportError } from "@/utils/reportError";
import { supabase } from '@/utils/supabase';
import { useEffect, useState } from 'react';

interface NotificationPreferences {
  quizEnabled: boolean;
  objectivesEnabled: boolean;
  wheelEnabled: boolean;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
}

export function useNotificationPreferences() {
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    quizEnabled: true,
    objectivesEnabled: true,
    wheelEnabled: true,
    quietHoursStart: null,
    quietHoursEnd: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        // Si pas de préférences, elles seront créées automatiquement par le trigger
      } else if (data) {
        setPreferences({
          quizEnabled: data.quiz_enabled,
          objectivesEnabled: data.objectives_enabled,
          wheelEnabled: data.wheel_enabled,
          quietHoursStart: data.quiet_hours_start,
          quietHoursEnd: data.quiet_hours_end,
        });
      }
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (
    key: keyof NotificationPreferences,
    value: boolean | string | null
  ) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return { success: false };

      // Mapping des clés camelCase vers snake_case
      const dbKey = {
        quizEnabled: 'quiz_enabled',
        objectivesEnabled: 'objectives_enabled',
        wheelEnabled: 'wheel_enabled',
        quietHoursStart: 'quiet_hours_start',
        quietHoursEnd: 'quiet_hours_end',
      }[key];

      const { error } = await supabase
        .from('notification_preferences')
        .update({ [dbKey]: value })
        .eq('user_id', user.id);

      if (error) throw error;

      // Mise à jour locale
      setPreferences(prev => ({ ...prev, [key]: value }));

      return { success: true };
    } catch (error) {
      reportError(error);
      return { success: false, error };
    }
  };

  return {
    preferences,
    loading,
    updatePreference,
    refetch: fetchPreferences,
  };
}
